import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CalendarDays, ChevronRight, Plus, RefreshCw, Search, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { MEETING_STATUS_LABELS, MEETING_STATUS_TONES, MEETING_VIEW_TABS, canExportGovernance, canManageMeetings, formatMoment, governanceError } from "../lib/governance";
import { Chip } from "../components/governance/GovernanceBits";
import ExportButton from "../components/governance/ExportButton";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Lock } from "lucide-react";

const EMPTY_TEXT = {
  upcoming: ["Aucune réunion à venir", "Planifiez la prochaine : objet, participants et ordre du jour, puis le compte rendu et les décisions suivent."],
  closed: ["Aucun compte rendu clos", "Une réunion close garde son compte rendu figé, ses décisions et ses actions."],
  all: ["Aucune réunion pour le moment", "Les réunions et leurs comptes rendus s'enregistrent ici."],
};

const StateChip = ({ m, testid }) => (
  <span className="inline-flex flex-wrap items-center gap-1.5">
    <Chip label={MEETING_STATUS_LABELS[m.status]} tone={MEETING_STATUS_TONES[m.status]} testid={testid} />
    {m.restricted && <Chip label="Restreinte" tone="slate"><Lock size={11} aria-hidden="true" /></Chip>}
  </span>
);

export default function GovernanceMeetings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = MEETING_VIEW_TABS.some(([id]) => id === params.get("view")) ? params.get("view") : "upcoming";
  const search = params.get("q") || "";
  const mine = params.get("mine") === "1";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const seq = useRef(0);
  const canManage = canManageMeetings(user);

  const update = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const id = ++seq.current;
    setPhase((p) => (p === "ready" ? p : "loading"));
    try {
      const { data: body } = await api.get("/governance/meetings", { params: { view, limit: 200, ...(search ? { q: search } : {}), ...(mine ? { mine: true } : {}) } });
      if (id !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (id !== seq.current) return;
      setError(governanceError(e, "Impossible de charger les réunions."));
      setPhase("error");
    }
  }, [view, search, mine]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const reset = () => { setSearchInput(""); update({ q: "", mine: "" }); };
  const open = (m) => navigate(`/gouvernance/reunions/${m.id}`);
  const filtered = !!search || mine;

  return (
    <div className="space-y-4" data-testid="governance-meetings-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Réunions</h1>
          <p className="text-sm text-slate-500">L'ordre du jour, le compte rendu, les décisions prises et les actions qui en sortent. La discussion elle-même reste dans votre outil de visio.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canExportGovernance(user) && <ExportButton kind="meetings" />}
          {canManage && <Button asChild data-testid="meeting-new"><Link to="/gouvernance/reunions/nouvelle"><Plus size={16} className="mr-1.5" />Nouvelle réunion</Link></Button>}
        </div>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" role="tablist" aria-label="Réunions">
        {MEETING_VIEW_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => update({ view: id === "upcoming" ? "" : id })} data-testid={`meetings-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${view === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {label}{data.counts?.[id] != null && <span className={`ml-1.5 tabular-nums ${view === id ? "text-white/80" : "text-slate-400"}`} data-testid={`meetings-count-${id}`}>{data.counts[id]}</span>}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou objet…" className="pl-9 h-9" aria-label="Rechercher une réunion" data-testid="meetings-search" />
        </div>
        <button type="button" aria-pressed={mine} onClick={() => update({ mine: mine ? "" : "1" })} data-testid="meetings-mine"
          className={`rounded-full px-3 py-1.5 text-sm font-medium ${mine ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>Mes réunions</button>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="meetings-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="meetings-empty">
          <CalendarDays className="mx-auto text-slate-300" size={26} />
          {filtered ? <><h3 className="font-medium text-slate-900">Aucune réunion ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></> : (
            <><h3 className="font-medium text-slate-900">{EMPTY_TEXT[view][0]}</h3><p className="text-sm text-slate-500 max-w-md mx-auto">{EMPTY_TEXT[view][1]}</p>
              {canManage && view !== "closed" && <Button asChild size="sm"><Link to="/gouvernance/reunions/nouvelle"><Plus size={14} className="mr-1.5" />Nouvelle réunion</Link></Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="meetings-table">
            <Table>
              <TableHeader><TableRow><TableHead>Réunion</TableHead><TableHead>Quand</TableHead><TableHead>État</TableHead><TableHead className="hidden lg:table-cell text-right">Participants</TableHead><TableHead className="hidden lg:table-cell text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer" onClick={() => open(m)} data-testid={`meeting-row-${m.number}`}>
                    <TableCell className="max-w-md"><div className="font-medium text-slate-900 truncate">{m.title}</div><div className="text-xs text-slate-500 tabular-nums">{m.number} · {m.kind_label}</div></TableCell>
                    <TableCell className="text-sm text-slate-700">{formatMoment(m.starts_at)}</TableCell>
                    <TableCell><StateChip m={m} testid={`meeting-state-${m.number}`} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-right text-sm tabular-nums">{m.participants_count || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden lg:table-cell text-right text-sm tabular-nums">{m.tasks_count || <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="meetings-cards">
            {data.items.map((m) => (
              <button key={m.id} type="button" onClick={() => open(m)} className="flex w-full items-start gap-3 px-4 py-3 text-left" data-testid={`meeting-card-${m.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900">{m.title}</div>
                  <div className="text-xs text-slate-500">{m.number} · {m.kind_label}</div>
                  <div className="text-sm text-slate-700">{formatMoment(m.starts_at)}</div>
                  <StateChip m={m} testid={`meeting-card-state-${m.number}`} />
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </button>
            ))}
          </Card>
          <p className="text-xs text-slate-500" data-testid="meetings-total">{data.total} réunion{data.total > 1 ? "s" : ""}</p>
        </>
      )}
    </div>
  );
}
